import AccessTime from '@mui/icons-material/AccessTime';
import { Box, Chip, Stack, Typography } from '@mui/material';
import {
  courseTypeIconNames,
  modalityIconMap,
  resourceIconMap,
} from '@/constants/resources/resources-icons';
import {
  courseTypesLabels,
  modalityTypesLabels,
} from '@/constants/catalog/course';
import type { DurationTypes } from '@/models/catalog/course';
import type { AcademicSituationDetails } from '@/models/learning/academic-situation';
import { formatDuration } from '@/utils/masks/time';
import { detailStatusBanner, detailStatusConfig } from './detail-status-config';

interface AcademicSituationSummaryProps {
  situation: AcademicSituationDetails;
}

const MetaItem = ({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    color="neutral.500"
  >
    {icon}
    <Typography variant="body1">{children}</Typography>
  </Stack>
);

export const AcademicSituationSummary = ({
  situation,
}: AcademicSituationSummaryProps) => {
  const { embedded_course } = situation;
  const status = detailStatusConfig[situation.current_status];
  const banner = detailStatusBanner(situation);

  const CourseTypeIcon =
    resourceIconMap[courseTypeIconNames[embedded_course.course_type]];

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: 'background.paper',
        border: ({ palette }) => `1px solid ${palette[status.palette].main}`,
      }}
    >
      <Stack spacing={2} p={3}>
        <Box>
          <Chip
            label={status.label}
            size="small"
            sx={{
              backgroundColor: status.backgroundColor,
              color: status.color,
              fontWeight: 600,
            }}
          />
        </Box>

        <Typography variant="h1">{embedded_course.name}</Typography>

        <Stack spacing={1}>
          {CourseTypeIcon && (
            <MetaItem icon={<CourseTypeIcon fontSize="small" />}>
              {courseTypesLabels[embedded_course.course_type]}
            </MetaItem>
          )}
          {embedded_course.duration && embedded_course.duration_type && (
            <MetaItem icon={<AccessTime fontSize="small" />}>
              {formatDuration(
                embedded_course.duration_type as DurationTypes,
                embedded_course.duration
              )}
            </MetaItem>
          )}
          <MetaItem icon={modalityIconMap[embedded_course.modality]}>
            {modalityTypesLabels[embedded_course.modality]}
          </MetaItem>
        </Stack>
      </Stack>

      {banner && (
        <Box
          px={3}
          py={1.5}
          sx={{
            backgroundColor: status.backgroundColor,
            color: status.color,
          }}
        >
          <Typography
            color={status.color}
            sx={(theme) => ({
              fontFamily: theme.typography.h2.fontFamily,
              fontWeight: 600,
              fontSize: 14,
              lineHeight: 1.5,
            })}
          >
            {banner}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
